import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Keyboard } from 'lucide-react';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

import type { DesignEditorApi } from './useDesignEditor';
import { BarButton } from './ui';

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
export const MOD = isMac ? '⌘' : 'Ctrl';

/**
 * Editor keyboard shortcuts. Listens in the capture phase and stops what it
 * handles, so the workspace canvas underneath (which has its own Delete and
 * Cmd+Z) never sees a keystroke meant for the design.
 */
export function useEditorShortcuts(ed: DesignEditorApi, open: boolean, onHelp: () => void): void {
    const edRef = useRef(ed);
    edRef.current = ed;
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e: KeyboardEvent) => {
            const editor = edRef.current;
            const el = document.activeElement as HTMLElement | null;
            const inField = !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable);
            const active = editor.canvas?.getActiveObject() as any;
            const editingText = !!active?.isEditing;
            if (inField || editingText) return;
            const mod = e.ctrlKey || e.metaKey;
            const key = e.key.toLowerCase();
            let handled = true;

            if (editor.cropping) {
                if (e.key === 'Enter') editor.applyCrop();
                else if (e.key === 'Escape') editor.cancelCrop();
                else handled = false;
            } else if (mod && key === 'z') {
                if (e.shiftKey) editor.redo(); else editor.undo();
            } else if (mod && key === 'y') editor.redo();
            else if (mod && key === 'c') editor.copy();
            else if (mod && key === 'x') { editor.copy(); editor.deleteSelection(); }
            else if (mod && key === 'v') editor.paste();
            else if (mod && key === 'd') editor.duplicate();
            else if (mod && key === 'a') editor.selectAll();
            else if (mod && key === 'g') { if (e.shiftKey) editor.ungroup(); else editor.group(); }
            else if (mod && e.shiftKey && key === 'l') editor.toggleLock();
            else if (mod && (e.key === ']' || e.code === 'BracketRight')) editor.order(e.shiftKey || e.altKey ? 'front' : 'forward');
            else if (mod && (e.key === '[' || e.code === 'BracketLeft')) editor.order(e.shiftKey || e.altKey ? 'back' : 'backward');
            else if (mod && (key === '=' || key === '+')) editor.zoomBy(1.2);
            else if (mod && key === '-') editor.zoomBy(1 / 1.2);
            else if ((mod && key === '0') || (e.shiftKey && e.code === 'Digit1')) editor.zoomFit();
            else if (!mod && (e.key === 'Delete' || e.key === 'Backspace')) editor.deleteSelection();
            else if (!mod && e.key.startsWith('Arrow')) {
                const step = e.shiftKey ? 10 : 1;
                const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
                const dy = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0;
                if (active) editor.nudge(dx, dy); else handled = false;
            } else if (e.key === 'Escape') {
                if (active) editor.select(null); else handled = false;
            } else if (e.key === 'Enter' && active) {
                if (typeof active.enterEditing === 'function') { active.enterEditing(); active.selectAll(); editor.canvas?.requestRenderAll(); } else if (active.type === 'image') editor.startCrop();
                else handled = false;
            } else if (e.key === '?' || (e.shiftKey && e.code === 'Slash')) onHelp();
            else handled = false;

            if (handled) {
                e.preventDefault();
                e.stopPropagation();
            }
        };
        window.addEventListener('keydown', onKey, true);
        return () => window.removeEventListener('keydown', onKey, true);
    }, [open, onHelp]);
}

const ROWS: Array<[string, string]> = [
    ['SC_UNDO', `${MOD} Z`],
    ['SC_REDO', `${MOD} ⇧ Z`],
    ['SC_COPY_PASTE', `${MOD} C / V`],
    ['SC_DUPLICATE', `${MOD} D`],
    ['SC_DELETE', '⌫'],
    ['SC_SELECT_ALL', `${MOD} A`],
    ['SC_GROUP', `${MOD} G`],
    ['SC_UNGROUP', `${MOD} ⇧ G`],
    ['SC_LOCK', `${MOD} ⇧ L`],
    ['SC_FORWARD', `${MOD} ]`],
    ['SC_BACKWARD', `${MOD} [`],
    ['SC_NUDGE', '← ↑ → ↓'],
    ['SC_NUDGE_BIG', '⇧ + ←'],
    ['SC_EDIT_TEXT', '↵'],
    ['SC_ZOOM', `${MOD} + / −`],
    ['SC_FIT', `${MOD} 0`],
    ['SC_PAN', '@KEY_SPACE'],
    ['SC_NO_SNAP', 'Alt'],
    ['SC_MULTI', '@KEY_SHIFT_CLICK'],
];

export const ShortcutsPopover: React.FC<{ open: boolean; onOpenChange: (v: boolean) => void }> = ({ open, onOpenChange }) => {
    const { t } = useTranslation();
    return (
        <Popover open={open} onOpenChange={onOpenChange}>
            <PopoverTrigger asChild>
                <BarButton title={t('DESIGN_EDITOR.SHORTCUTS')}><Keyboard /></BarButton>
            </PopoverTrigger>
            <PopoverContent align="end" sideOffset={8} className="w-[300px] p-3">
                <div className="mb-2 text-xs font-medium text-foreground">{t('DESIGN_EDITOR.SHORTCUTS')}</div>
                <div className="flex flex-col">
                    {ROWS.map(([k, keys]) => (
                        <div key={k} className="flex items-center justify-between gap-3 py-1 text-xs">
                            <span className="text-muted-foreground">{t(`DESIGN_EDITOR.${k}`)}</span>
                            <kbd className="rounded-md border bg-foreground/[0.04] px-1.5 py-0.5 font-sans text-[11px] text-foreground">{keys.startsWith('@') ? t(`DESIGN_EDITOR.${keys.slice(1)}`) : keys}</kbd>
                        </div>
                    ))}
                </div>
            </PopoverContent>
        </Popover>
    );
};
